/**
 * Wire-shape helpers for the organization page.
 *
 * The /update-org endpoint (dakasa-enterprise-api, EditOrganizationReq) binds
 * `phoneArea` and `phoneNumber` as strings and reads the logo exclusively from
 * `avatarManifestUrl`. Two mismatches used to make "Salvar" on the org page fail
 * with a red "Informações mal inseridas" even though the logo had already been
 * persisted by the compressor-conclude (`UPDATE org SET avatar`):
 *
 *  1. `InputTel.onChange` hands the phone parts back as numbers, and the page
 *     sent them raw. A JSON number decoded into a Go `string` field errors out in
 *     `c.ShouldBind` before any handler logic runs, so every save answered 400.
 *  2. The page sent the logo under an `avatar` key, which the handler never
 *     reads, so the PATCH never actually carried the logo (only the async
 *     compressor-conclude did). On reload the avatar was there, which is what made
 *     the error read as a false negative.
 *
 * Keeping the contract in one pure, tested place stops both from regressing.
 */
export interface OrgContactAvatarInput {
  phoneAreaCode: number;
  phoneNumber: number;
  organizationCountry?: string;
  phoneCountry?: string;
  /** Manifest URL for a freshly uploaded logo, when the save follows an upload. */
  avatarManifestUrl?: string;
  /** The logo already on the organization, used when no new upload happened. */
  currentAvatar: string;
}

export interface OrgContactAvatarFields {
  phoneCountry: string;
  phoneArea: string;
  phoneNumber: string;
  avatarManifestUrl: string;
}

export interface OrgAvatarUpdatePayload {
  status: string;
  manifest: string;
}

export interface LatestOrgAvatarSave {
  current: (avatarManifestUrl: string | undefined) => unknown;
}

export function buildOrgContactAvatarFields(input: OrgContactAvatarInput): OrgContactAvatarFields {
  const area = input.phoneAreaCode > 0 ? String(input.phoneAreaCode) : "";
  const number = input.phoneNumber > 0 ? String(input.phoneNumber) : "";
  const brazilianOrganization = /^(br|bra|brasil|brazil)$/i.test(
    input.organizationCountry?.trim() || "",
  );
  const missingCountry = !input.phoneCountry?.trim();
  return {
    phoneCountry:
      missingCountry && brazilianOrganization && /^\d{2}$/.test(area) && /^\d{8,9}$/.test(number)
        ? "+55"
        : input.phoneCountry || "",
    phoneArea: area,
    phoneNumber: number,
    avatarManifestUrl: input.avatarManifestUrl || input.currentAvatar,
  };
}

/**
 * Build the long-lived RTA listener around a mutable callback reference.
 *
 * The listener is registered before the organization request finishes. Closing
 * over the first render's save function therefore replays the zero-initialized
 * form when compression concludes, including an empty email. Dereferencing the
 * callback at event time keeps the socket subscription stable while saving the
 * latest form state.
 */
export function createOrgAvatarUpdateHandler(saveRef: LatestOrgAvatarSave) {
  return (data: OrgAvatarUpdatePayload) => {
    if (data.status !== "success" || !data.manifest) return false;

    void saveRef.current(data.manifest);
    return false;
  };
}
